import React, { useEffect, useRef } from "react";
import moment, { Moment } from "moment";
import { DatePicker } from "antd";
import { useMount, useRequest, useSetState } from "ahooks";
import TopFilterBase from "../../../commonComponents/listBase/TopFilterBase";
import InputLabel from "../../../../base/basicComponents/inputLabel";
import NormalSelect, { NormalSelectV5 } from "../../../../base/basicComponents/select";
import Buttons from "../../../../base/basicComponents/button";
import myRequestListService from "../../../../service/myRequestListService/index";
import styles from "./index.module.scss";
import "../../../../assets/css/style.css";
import { FilterOptionList, SelectOptionList, TopFilterProps } from "../types";
import { RangeValue } from "rc-picker/lib/interface";
import { processStatusOptions, processScopeOptions } from "./utils";
import { getDate } from "app/pages/myRequestList/utils";
import { isArray } from "lodash-es";
import { MyRequestTopFilterResponse } from "service/myRequestListService/types";

const dateFormat = "MM/DD/YYYY";
const {
  handleOptionList,
  sortList,
  handleChange,
  handleDeSelect,
  handleFilterOption,
  sortProcessStatusList,
  handleFilterOptionV5,
} = TopFilterBase();

export default function TopFilter(props: TopFilterProps) {
  const { handleFilterClick, isOpex, setLoading } = props;
  const [threeMonthsAgoDate, currentDate] = getDate();
  // save the process category list when get top filter api
  const processCategoryRef = useRef<MyRequestTopFilterResponse>({
    processScope: [],
    processCategories: [],
    allProcessCategories: [],
  });

  /* state about selects' data */
  const [filterOptionList, setFilterOptionList] = useSetState<FilterOptionList>({
    processInitiateDateFrom: threeMonthsAgoDate,
    processInitiateDateTo: currentDate,
    processScope: processScopeOptions,
    accountAbbr: [],
    processCategory: [],
    processStatus: processStatusOptions,
  });

  /* state about select's selected data */
  const [selectOptions, setSelectOptions] = useSetState<SelectOptionList>({
    processInitiateDateFrom: threeMonthsAgoDate,
    processInitiateDateTo: currentDate,
    processScope: 1,
    accountAbbr: [],
    processCategory: [],
    processStatus: [],
  });

  const handleClearClick = () => {
    const defaultOption = {
      processInitiateDateFrom: threeMonthsAgoDate,
      processInitiateDateTo: currentDate,
      processScope: 1,
      accountAbbr: [],
      processCategory: [],
      processStatus: [],
    };
    setSelectOptions(defaultOption);
    setFilterOptionList({
      accountAbbr: filterOptionList.accountAbbr.sort(sortList),
      processCategory: filterOptionList.processCategory.sort(sortList),
      processStatus: filterOptionList.processStatus.sort(sortProcessStatusList),
      // make my request only on the top when clear
      processScope: filterOptionList.processScope.sort((a, b) => sortList(b, a)),
    });
    handleFilterClick(filterOptionList, defaultOption);
  };

  const getAccountAbbr = () => {
    if (isOpex) {
      runGetOpexAccountAbbrList(2);
    } else {
      runGetAccountAbbrList();
    }
  };

  const { run: runGetOpexAccountAbbrList, loading: loadingBatch1 } = useRequest(
    myRequestListService.getAllAccountAbbreviationsNew,
    {
      manual: true,
      onSuccess: (response) => {
        const option = handleOptionList(response, true);
        handleFilterClick(
          {
            ...filterOptionList,
            accountAbbr: option,
          },
          {
            ...selectOptions,
            accountAbbr: [],
          }
        );
        setFilterOptionList({ accountAbbr: option });
        setSelectOptions({
          accountAbbr: [],
        });
      },
      onError: (error) => {
        console.log(error);
      },
    }
  );

  const { run: runGetAccountAbbrList, loading: loadingBatch2 } = useRequest(
    myRequestListService.getAccountAbbrListNew,
    {
      manual: true,
      onSuccess: (response) => {
        const option = handleOptionList(response, true);
        handleFilterClick(
          {
            ...filterOptionList,
            accountAbbr: option,
          },
          {
            ...selectOptions,
            accountAbbr: [],
          }
        );
        setFilterOptionList({ accountAbbr: option });
        setSelectOptions({
          accountAbbr: [],
        });
      },
      onError: (error) => {
        console.log(error);
      },
    }
  );

  const { run: runGetMyRequestTopFilter, loading: loadingBatch3 } = useRequest(
    myRequestListService.getMyRequestTopFilterNew,
    {
      manual: true,
      onSuccess: (response) => {
        const { processScope, processCategories } = response;
        processCategoryRef.current = response;
        setFilterOptionList({
          processScope: handleOptionList(processScope, false, true),
          processCategory: handleOptionList(processCategories, false),
        });
      },
    }
  );

  const handleDatePickerChange = (date: RangeValue<Moment>) => {
    if (!isArray(date) || !date[0] || !date[1]) return;
    const dateFormat = "YYYY-MM-DD";
    const processInitiateDateFrom = date[0].format(dateFormat);
    const processInitiateDateTo = date[1].format(dateFormat);
    setSelectOptions({
      processInitiateDateFrom,
      processInitiateDateTo,
    });
  };

  const handleSelectAll = (
    filterName: Extract<keyof FilterOptionList, "processCategory" | "accountAbbr" | "processStatus">
  ) => {
    const optionList = filterOptionList[filterName];
    const allOptionsValue = optionList.map((item) => item.value);
    const isProcessStatus = filterName === "processStatus";

    setSelectOptions((prev) => ({
      ...prev,
      [filterName]: allOptionsValue,
    }));
    setFilterOptionList((prev) => ({
      ...prev,
      [filterName]: [...handleChange(optionList, allOptionsValue, isProcessStatus)],
    }));
  };

  const handleDeselectAll = (
    filterName: Extract<keyof FilterOptionList, "processCategory" | "accountAbbr" | "processStatus">
  ) => {
    const optionList = filterOptionList[filterName];
    const isProcessStatus = filterName === "processStatus";

    setSelectOptions((prev) => ({
      ...prev,
      [filterName]: [],
    }));
    setFilterOptionList((prev) => ({
      ...prev,
      [filterName]: isProcessStatus
        ? [...optionList.sort((s, t) => sortProcessStatusList(s, t))]
        : [...optionList.sort((s, t) => sortList(s, t))],
    }));
  };

  /* function about when page init get filter selects option list */
  useMount(() => {
    getAccountAbbr();
    runGetMyRequestTopFilter();
  });

  useEffect(() => {
    setLoading(loadingBatch1 || loadingBatch2 || loadingBatch3);
  }, [loadingBatch1, loadingBatch2, loadingBatch3]);

  return (
    <div className={styles.topFilterContainer}>
      <div className={styles.topFilterSelects}>
        <div className={styles.topFilterSelectItem}>
          <InputLabel
            text="Process Initiate Date"
            className={`${styles.topFilterSelectLabel} fs-14`}
          />
          <DatePicker.RangePicker
            value={[
              moment(selectOptions.processInitiateDateFrom, "YYYY-MM-DD"),
              moment(selectOptions.processInitiateDateTo, "YYYY-MM-DD"),
            ]}
            onChange={handleDatePickerChange}
            format={dateFormat}
            allowClear={false}
            className="topFilterDatePicker"
            popupStyle={{ zIndex: "100000" }}
            data-testid="my-request-filter-date-datepicker"
          />
        </div>
        <div className={styles.topFilterSelectItem}>
          <InputLabel text="Process Scope" className={`${styles.topFilterSelectLabel} fs-14`} />
          <NormalSelect
            value={selectOptions.processScope}
            optionList={filterOptionList.processScope}
            className="topFilterSelectInput"
            showArrow={true}
            maxTagCount="responsive"
            testid="my-request-filter-scope-select"
            handleChange={(e: number) => {
              const optionList = filterOptionList.processScope;
              const targetCategories =
                e === 2
                  ? processCategoryRef.current.allProcessCategories
                  : processCategoryRef.current.processCategories;
              setSelectOptions({
                processScope: e,
                processCategory: [],
              });
              setFilterOptionList({
                processScope: [
                  ...handleChange(optionList, e),
                  ...optionList.sort((s, t) => sortList(s, t)),
                ],
                processCategory: handleOptionList(targetCategories, false),
              });
            }}
          />
        </div>
        <div className={styles.topFilterSelectItem}>
          <InputLabel text="Account Code" className={`${styles.topFilterSelectLabel} fs-14`} />
          <NormalSelectV5
            value={selectOptions.accountAbbr}
            optionList={filterOptionList.accountAbbr}
            className="topFilterSelectInput"
            mode="multiple"
            showArrow={true}
            maxTagCount="responsive"
            testid="my-request-filter-account-select"
            handleChange={(e: number[]) => {
              const optionList = filterOptionList.accountAbbr;
              setSelectOptions({
                accountAbbr: e,
              });
              setFilterOptionList({
                accountAbbr: [
                  ...handleChange(optionList, e),
                  ...optionList.sort((s, t) => sortList(s, t)),
                ],
              });
            }}
            handleFilterOption={(input, option) => handleFilterOptionV5(input, option)}
            isNeedSelectAll={filterOptionList.accountAbbr.length > 0}
            handleSelectAll={() => handleSelectAll("accountAbbr")}
            handleDeselectAll={() => handleDeselectAll("accountAbbr")}
          />
        </div>
        <div className={styles.topFilterSelectItem}>
          <InputLabel text="Process Category" className={`${styles.topFilterSelectLabel} fs-14`} />
          <NormalSelect
            value={selectOptions.processCategory}
            optionList={filterOptionList.processCategory}
            className="topFilterSelectInput"
            mode="multiple"
            showArrow={true}
            maxTagCount="responsive"
            key={selectOptions.processScope}
            testid="my-request-filter-category-select"
            handleChange={(e: string[]) => {
              const optionList = filterOptionList.processCategory;
              setSelectOptions({
                processCategory: e,
              });
              setFilterOptionList({
                processCategory: [
                  ...handleChange(optionList, e),
                  ...optionList.sort((s, t) => sortList(s, t)),
                ],
              });
            }}
            handleDeSelect={(e: string) => {
              const selectList = [...selectOptions.processCategory];
              setSelectOptions({
                processCategory: handleDeSelect(e, selectList),
              });
            }}
            handleFilterOption={(input: any, option: any) => handleFilterOption(input, option)}
            isNeedSelectAll={filterOptionList.processCategory.length > 0}
            handleSelectAll={() => handleSelectAll("processCategory")}
            handleDeselectAll={() => handleDeselectAll("processCategory")}
          />
        </div>
        <div className={styles.topFilterSelectItem}>
          <InputLabel text="Process Status" className={`${styles.topFilterSelectLabel} fs-14`} />
          <NormalSelect
            value={selectOptions.processStatus}
            optionList={filterOptionList.processStatus}
            className="topFilterSelectInput"
            mode="multiple"
            showArrow={true}
            maxTagCount="responsive"
            testid="my-request-filter-status-select"
            handleChange={(e: number[]) => {
              const optionList = filterOptionList.processStatus;
              setSelectOptions({
                processStatus: e,
              });
              setFilterOptionList({
                processStatus: [
                  ...handleChange(optionList, e, true),
                  ...optionList.sort((s, t) => sortProcessStatusList(s, t)),
                ],
              });
            }}
            handleDeSelect={(e: number) => {
              const selectList = [...selectOptions.processStatus];
              setSelectOptions({
                processStatus: handleDeSelect(e, selectList),
              });
            }}
            handleFilterOption={(input: any, option: any) => handleFilterOption(input, option)}
            isNeedSelectAll={filterOptionList.processStatus.length > 0}
            handleSelectAll={() => handleSelectAll("processStatus")}
            handleDeselectAll={() => handleDeselectAll("processStatus")}
          />
        </div>
      </div>
      <div className={styles.topFilterBtns}>
        <Buttons
          color="blue"
          size="middle"
          onClick={() => handleFilterClick(filterOptionList, selectOptions)}
          testid="my-request-filter-btn"
        >
          Filter
        </Buttons>
        <Buttons
          color="grey"
          size="middle"
          onClick={handleClearClick}
          testid="my-request-filter-clear-btn"
        >
          Clear
        </Buttons>
      </div>
    </div>
  );
}
